import { createApi } from "@reduxjs/toolkit/query/react";
import { ApiEndpoints, baseQueryWithAuth } from "../../../core";
import { ApiResponse, IMuscle } from "../../../types";

type GetMusclesArg = void | {
  page?: number;
  limit?: number;
};

export const muscleApi = createApi({
  reducerPath: "muscleApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Muscles"],

  endpoints: (builder) => ({
    getMuscles: builder.query<IMuscle[], GetMusclesArg>({
      query: (params) => {
        let page: number | undefined;
        let limit: number | undefined;

        if (typeof params === "object" && params !== null) {
          page = params.page;
          limit = params.limit;
        }

        const search = new URLSearchParams();
        if (page !== undefined) search.append("page", String(page));
        if (limit !== undefined) {
          search.append("limit", String(limit));
        } else {
          // Backwards-compatible default to fetch all when no limit is provided
          search.append("limit", "-1");
        }

        return {
          url: `${ApiEndpoints.getAllMuscles}?${search.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (res: ApiResponse<IMuscle[]>) => {
        return res?.result ?? res?.results ?? [];
      },
      providesTags: ["Muscles"],
    }),

    getMuscleById: builder.query<IMuscle, string>({
      query: (id) => ({
        url: `${ApiEndpoints.muscle}/${id}`,
        method: "GET",
      }),
      transformResponse: (res: ApiResponse<IMuscle>) => {
        return res?.result!;
      },
      providesTags: (_result, _error, id) => [{ type: "Muscles", id }],
    }),

    createMuscle: builder.mutation<ApiResponse<IMuscle>, Partial<IMuscle>>({
      query: (data) => ({
        url: ApiEndpoints.createMuscle,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Muscles"],
    }),

    editMuscle: builder.mutation<
      ApiResponse<IMuscle>,
      { id: string; data: Partial<IMuscle> }
    >({
      query: ({ id, data }) => ({
        url: `${ApiEndpoints.editMuscle}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "Muscles",
        { type: "Muscles", id },
      ],
    }),

    deleteMuscle: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `${ApiEndpoints.deleteMuscle}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Muscles"],
    }),
  }),
});

export const {
  useGetMusclesQuery,
  useGetMuscleByIdQuery,
  useCreateMuscleMutation,
  useEditMuscleMutation,
  useDeleteMuscleMutation,
} = muscleApi;
